/**
 * File: LeadPrimitive.tsx
 * Path: apps/admin/src/shared/components/LeadPrimitive.tsx
 * Description: Small pieces shared by the Leads pages: field styles, the card
 * frame, the stage badge and the Pipeline / Find leads switcher.
 */

import { tabClass } from "@/shared/utils/tabClassUtils";
import React, { useId } from "react";
import { NavLink } from "react-router-dom";
import { ADMIN_PATHS, LEAD_STAGE_LABELS, type LeadStage } from "@idk/config";
import { cn } from "@idk/utils";

export const fieldClass =
  "h-11 w-full rounded-none border border-border/50 bg-background/50 px-stack-sm text-sm";
export const labelClass = "text-xs font-medium text-foreground block";

const STAGE_TONE: Record<LeadStage, string> = {
  new: "bg-tone-blue/10 text-tone-blue",
  contacted: "bg-tone-amber/10 text-tone-amber",
  talking: "bg-tone-violet/10 text-tone-violet",
  quoted: "bg-tone-cyan/10 text-tone-cyan",
  won: "bg-tone-green/10 text-tone-green",
  lost: "bg-secondary text-muted-foreground",
};

export const StageBadge: React.FC<{ stage: LeadStage; className?: string }> = ({
  stage,
  className,
}) => (
  <span
    className={cn(
      "inline-flex items-center rounded-none px-stack-xs py-0.5 text-xs font-bold uppercase tracking-wider",
      STAGE_TONE[stage],
      className,
    )}
  >
    {LEAD_STAGE_LABELS[stage]}
  </span>
);

/** The Pipeline page's tabs: the deals, and the Google Maps search. Same style as the other pages. */
export const LeadsTabs: React.FC = () => (
  <nav aria-label="Pipeline sections" className="flex gap-stack-xs overflow-x-auto border-b border-border/50">
    <NavLink to={ADMIN_PATHS.leads.root} end className={({ isActive }) => tabClass(isActive)}>
      Deals
    </NavLink>
    <NavLink to={ADMIN_PATHS.leads.find} className={({ isActive }) => tabClass(isActive)}>
      Find leads
    </NavLink>
  </nav>
);

/** One question answered from a drop-down, instead of a row of buttons. */
export const ChoiceSelect: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
  /** Shown (and not pickable) until something is chosen. */
  placeholder?: string;
}> = ({ label, value, onChange, options, placeholder }) => {
  const id = useId();
  return (
    <div className="space-y-stack-xs">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <select id={id} className={fieldClass} value={value} onChange={(event) => onChange(event.target.value)}>
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};
